import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GAME_MODES, GameMode } from '../types';
import '../App.css';

const Results: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [missedStates, setMissedStates] = useState<string[]>([]);

  const score = parseInt(searchParams.get('score') || '0');
  const timeSeconds = parseInt(searchParams.get('time') || '0');
  const mode = (searchParams.get('mode') || 'full_usa') as GameMode;

  // Load missed states from sessionStorage
  useEffect(() => {
    const storedMissedStates = sessionStorage.getItem('missedStates');
    if (storedMissedStates) {
      setMissedStates(JSON.parse(storedMissedStates));
      // Clear from sessionStorage after loading
      sessionStorage.removeItem('missedStates');
    }
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPerformanceMessage = (score: number): string => {
    if (score >= 45) return 'Outstanding! You\'re a geography expert!';
    if (score >= 35) return 'Great job! You know your geography well!';
    if (score >= 25) return 'Good work! Keep practicing!';
    if (score >= 15) return 'Not bad! Room for improvement!';
    return 'Keep learning! Practice makes perfect!';
  };

  return (
    <div className="results-container">
      <div className="results-content">
        <div className="results-header">
          <h1 className="results-title">Game Complete!</h1>
          <p className="results-subtitle">{GAME_MODES[mode]}</p>
        </div>

        <div className="results-stats">
          <div className="results-stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <div className="stat-value-large">{score}</div>
              <div className="stat-label">Final Score</div>
            </div>
          </div>

          <div className="results-stat-card">
            <div className="stat-icon">⏱️</div>
            <div className="stat-content">
              <div className="stat-value-large">{formatTime(timeSeconds)}</div>
              <div className="stat-label">Time Taken</div>
            </div>
          </div>
        </div>

        <div className="performance-message">
          <p>{getPerformanceMessage(score)}</p>
        </div>

        {missedStates.length > 0 && (
          <div className="missed-states-section" style={{
            background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '24px',
            border: '2px solid #ef5350'
          }}>
            <h3 style={{ marginTop: 0, color: '#c62828', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>❌</span> Missed {missedStates.length === 1 ? 'Location' : 'Locations'}
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '8px',
              marginTop: '12px'
            }}>
              {missedStates.map((stateName, index) => (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#c62828',
                    fontWeight: 500,
                    border: '1px solid #ef9a9a'
                  }}
                >
                  {stateName}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="results-actions">
          <button
            className="btn-primary"
            onClick={() => navigate(`/game?mode=${mode}&difficulty=${searchParams.get('difficulty') || 'hard'}`)}
          >
            Play Again
          </button>
          <button
            className="btn-secondary"
            onClick={() => navigate('/')}
          >
            Main Menu
          </button>
        </div>
      </div>
    </div>
  );
};

export default Results;
